<script lang="ts">
export default {
  name: 'BrowseTemplatesModal',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Trash2 } from '@lucide/vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppButton from '@/components/v2/AppButton.vue';
import DashboardTemplateService from '@/services/v2/DashboardTemplateService';
import TemplateThumbnail from './TemplateThumbnail.vue';
import type { DashboardTemplate } from '@/types/v2/DashboardTemplate';

const props = defineProps<{
  show: boolean;
  isAdmin?: boolean;
  autoOpened?: boolean;
}>();

const emit = defineEmits<{
  close: [];
  apply: [templateId: number];
}>();

const templates = ref<DashboardTemplate[]>([]);
const loading = ref(false);

const sortedTemplates = computed(() => [...templates.value].sort((a, b) => a.title.localeCompare(b.title)));

watch(
  () => props.show,
  async (visible) => {
    if (visible) {
      loading.value = true;
      try {
        templates.value = await DashboardTemplateService.list();
      } catch {
        templates.value = [];
      } finally {
        loading.value = false;
      }
    }
  },
);

async function deleteTemplate(id: number) {
  templates.value = templates.value.filter((t) => t.id !== id);
  try {
    await DashboardTemplateService.remove(id);
  } catch {
    templates.value = await DashboardTemplateService.list();
  }
}
</script>

<template>
  <AppModal
    :show="show"
    title="Template Library"
    cancel-button-label="Close"
    :handle-cancel="() => emit('close')"
    size="xl">
    <template #content>
      <div
        v-if="loading"
        class="flex flex-col gap-3">
        <div
          v-for="n in 3"
          :key="n"
          class="h-20 skeleton rounded-lg"></div>
      </div>

      <div
        v-else-if="sortedTemplates.length === 0"
        class="py-8 text-center text-base-content/50">
        <p>No templates available yet.</p>
      </div>

      <div
        v-else
        class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div
          v-for="tmpl in sortedTemplates"
          :key="tmpl.id"
          class="flex flex-col justify-between gap-3 rounded-lg border p-4">
          <div>
            <TemplateThumbnail
              :layout="tmpl.layout"
              class="mb-3" />
            <div class="font-medium">{{ tmpl.title }}</div>
            <div
              v-if="tmpl.description"
              class="mt-1 text-sm text-base-content/60">
              {{ tmpl.description }}
            </div>
            <div class="mt-2 text-xs text-base-content/40">
              {{ tmpl.layout.columns.length }} column{{ tmpl.layout.columns.length !== 1 ? 's' : '' }},
              {{ tmpl.layout.columns.reduce((sum, col) => sum + col.widgets.length, 0) }} widget{{
                tmpl.layout.columns.reduce((sum, col) => sum + col.widgets.length, 0) !== 1 ? 's' : ''
              }}
            </div>
          </div>
          <div class="flex items-center justify-between">
            <AppButton
              v-if="isAdmin"
              class="text-error btn-ghost"
              @click="deleteTemplate(tmpl.id)">
              <Trash2 :size="14" />
            </AppButton>
            <span v-else></span>
            <AppButton
              primary
              @click="emit('apply', tmpl.id)">
              Apply
            </AppButton>
          </div>
        </div>
      </div>
    </template>
    <template #actions>
      <AppButton @click="emit('close')">
        {{ autoOpened ? 'Start from scratch' : 'Close' }}
      </AppButton>
    </template>
  </AppModal>
</template>
